import { useEffect, useState, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import { VmSnapshot } from '../types/models/VirtualMachine';
import {
  ApiResponseVmSnapshotList,
  ApiResponseVmSnapshotCreate,
  ApiResponseVmSnapshotDelete,
} from '../types/api/ApiResponseVirtualMachine';

interface UseVmSnapshotsReturn {
  snapshots: VmSnapshot[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  createSnapshot: (vmId: number, name: string) => Promise<void>;
  deleteSnapshot: (vmId: number, snapshotId: number) => Promise<void>;
}

export const useVmSnapshots = (vmId: string | number): UseVmSnapshotsReturn => {
  const [snapshots, setSnapshots] = useState<VmSnapshot[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchSnapshots = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiFetch(`/datacenters/vms/${vmId}/snapshots`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = (await response.json()) as ApiResponseVmSnapshotList;
      if (data.status === 'success') {
        setSnapshots(data.data.snapshots);
      } else {
        setSnapshots([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load snapshots'));
    } finally {
      setIsLoading(false);
    }
  }, [vmId, apiFetch]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      fetchSnapshots();
    }
  }, [auth.isAuthenticated, fetchSnapshots]);

  const createSnapshot = useCallback(
    async (targetVmId: number, name: string) => {
      const response = await apiFetch(`/datacenters/vms/${targetVmId}/snapshots`, {
        method: 'POST',
        body: JSON.stringify({ name }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = (await response.json()) as ApiResponseVmSnapshotCreate;
      if (data.status !== 'success') {
        throw new Error(data.message || 'Failed to create snapshot');
      }

      await fetchSnapshots();
    },
    [apiFetch, fetchSnapshots],
  );

  const deleteSnapshot = useCallback(
    async (targetVmId: number, snapshotId: number) => {
      const response = await apiFetch(`/datacenters/vms/${targetVmId}/snapshots/${snapshotId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = (await response.json()) as ApiResponseVmSnapshotDelete;
      if (data.status !== 'success') {
        throw new Error(data.message || 'Failed to delete snapshot');
      }

      await fetchSnapshots();
    },
    [apiFetch, fetchSnapshots],
  );

  return { snapshots, isLoading, error, refetch: fetchSnapshots, createSnapshot, deleteSnapshot };
};
